import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { adminPlanAddonApi } from '../../api/adminSubscriptionPlan.api';
import { apiHandler, unwrapData } from '../../utils/apiHandler';
import './subscription-plans.css';

const emptyForm = {
  name: '',
  code: '',
  description: '',
  price: 0,
  interval: 'month',
  isFutureRelease: true,
  isActive: true,
  displayOrder: 0,
  stripeProductId: '',
  stripePriceId: ''
};

export default function PlanAddonEditor() {
  const { addonId } = useParams();
  const navigate = useNavigate();
  const isNew = addonId === 'new';
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isNew) return;
    const load = async () => {
      const result = await apiHandler(() => adminPlanAddonApi.getById(addonId));
      if (result.success) {
        setForm({ ...emptyForm, ...unwrapData(result) });
      } else {
        setError(result.message || 'Failed to load add-on');
      }
      setLoading(false);
    };
    load();
  }, [addonId, isNew]);

  const save = async () => {
    setSaving(true);
    setError('');
    const payload = {
      ...form,
      price: Number(form.price),
      displayOrder: Number(form.displayOrder || 0)
    };
    const result = await apiHandler(() => (
      isNew
        ? adminPlanAddonApi.create(payload)
        : adminPlanAddonApi.update(addonId, payload)
    ));
    if (result.success) {
      navigate('/plan-addons');
    } else {
      setError(result.message || 'Save failed');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="sp-page sp-loading">
        <i className="fa-solid fa-spinner fa-spin" /> Loading add-on...
      </div>
    );
  }

  return (
    <div className="sp-page">
      <PageHeader
        title={isNew ? 'Create Plan Add-on' : `Edit ${form.name}`}
        subtitle="Optional add-on shown on the billing page"
        actions={(
          <button type="button" className="sp-btn sp-btn-secondary" onClick={() => navigate('/plan-addons')}>
            <i className="fa-solid fa-arrow-left" /> All add-ons
          </button>
        )}
      />

      {error && <div className="sp-alert sp-alert-danger">{error}</div>}

      <div className="sp-editor-layout">
        <div className="sp-card">
          <div className="sp-card-header">
            <i className="fa-solid fa-puzzle-piece" />
            <h4>Add-on details</h4>
          </div>
          <div className="sp-form-grid-2">
            <div className="sp-form-row">
              <label>Name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="sp-form-row">
              <label>Code</label>
              <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="TORNADO_ALERT" />
            </div>
          </div>
          <div className="sp-form-row">
            <label>Description</label>
            <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="sp-form-grid-2">
            <div className="sp-form-row">
              <label>Price (USD)</label>
              <input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            </div>
            <div className="sp-form-row">
              <label>Interval</label>
              <select value={form.interval} onChange={(e) => setForm({ ...form, interval: e.target.value })}>
                <option value="month">Monthly</option>
                <option value="year">Annual</option>
              </select>
            </div>
            <div className="sp-form-row">
              <label>Display order</label>
              <input type="number" value={form.displayOrder} onChange={(e) => setForm({ ...form, displayOrder: e.target.value })} />
            </div>
          </div>
          <div className="sp-form-row">
            <label>Stripe product ID</label>
            <input value={form.stripeProductId} onChange={(e) => setForm({ ...form, stripeProductId: e.target.value })} placeholder="prod_..." />
          </div>
          <div className="sp-form-row">
            <label>Stripe price ID</label>
            <input value={form.stripePriceId} onChange={(e) => setForm({ ...form, stripePriceId: e.target.value })} placeholder="price_..." />
          </div>
          <div className="sp-toggle-row">
            <label className="sp-toggle">
              <input type="checkbox" checked={!!form.isFutureRelease} onChange={(e) => setForm({ ...form, isFutureRelease: e.target.checked })} />
              Show as future release
            </label>
            <label className="sp-toggle">
              <input type="checkbox" checked={!!form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
              Active
            </label>
          </div>
        </div>

        <div className="sp-preview-panel">
          <div className="sp-preview-label">Preview</div>
          <div className="sp-addon-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <h3>{form.name || 'Add-on name'}</h3>
              {form.isFutureRelease && <span className="sp-badge sp-badge-future">Future Release</span>}
            </div>
            <span className="sp-addon-code">{form.code || 'CODE'}</span>
            <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>{form.description || 'Description'}</p>
            <div className="sp-addon-price">
              ${Number(form.price || 0).toFixed(2)}
              <small> / {form.interval}</small>
            </div>
          </div>
        </div>
      </div>

      <div className="sp-actions">
        <button type="button" className="sp-btn sp-btn-secondary" onClick={() => navigate('/plan-addons')}>
          Cancel
        </button>
        <button type="button" className="sp-btn sp-btn-primary" disabled={saving} onClick={save}>
          {saving ? 'Saving...' : 'Save add-on'}
        </button>
      </div>
    </div>
  );
}
